import { AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatTime, resolveCheckValue } from '../utils/formatters';
import { useLongPress } from '@/shared/hooks/useLongPress';
import type { Collect } from '../types';

interface DayTimelineCardProps {
  date: string;
  dayLabel: string;
  dateLabel: string;
  collects: Collect[];
  isToday?: boolean;
  totalLabel?: string;
  onCollectClick?: (collect: Collect) => void;
  onCollectPeekEnd?: () => void;
}

const INCONSISTENCY_MESSAGE = 'Registro incompleto: número ímpar de marcações neste dia.';

function slotLabel(index: number, collect: Collect): string {
  const check = resolveCheckValue(collect);
  if (check === 'in') return `Entrada ${Math.ceil((index + 1) / 2)}`;
  if (check === 'out') return `Saída ${Math.ceil(index / 2)}`;
  const pairIndex = Math.floor(index / 2) + 1;
  return index % 2 === 0 ? `Entrada ${pairIndex}` : `Saída ${pairIndex}`;
}

function isInconsistent(collects: Collect[]): boolean {
  return collects.length > 0 && collects.length % 2 !== 0;
}

interface CollectChipProps {
  collect: Collect;
  index: number;
  isLast: boolean;
  inconsistent: boolean;
  onTap: () => void;
  onLongPressRelease?: () => void;
}

function CollectChip({ collect, index, isLast, inconsistent, onTap, onLongPressRelease }: CollectChipProps) {
  const { firedRef, ...longPressHandlers } = useLongPress(onTap, { onRelease: onLongPressRelease });
  const isOddLast = inconsistent && isLast;

  const handleClick = () => {
    if (firedRef.current) {
      // Long press already opened the popup and onRelease already closed it.
      // Suppress the native click event that follows pointer-up to avoid re-opening.
      firedRef.current = false;
      return;
    }
    onTap();
  };

  return (
    <button
      type="button"
      className={cn(
        'flex-none select-none rounded-lg border p-2 text-center min-w-[72px]',
        'transition-colors active:scale-95',
        isOddLast
          ? 'border-amber-200 bg-amber-50 hover:bg-amber-100 dark:border-amber-800/40 dark:bg-amber-950/40 dark:hover:bg-amber-900/40'
          : 'border-slate-100 bg-slate-50 hover:bg-slate-100',
      )}
      onClick={handleClick}
      aria-label={`${slotLabel(index, collect)} às ${formatTime(collect.time)} — ver detalhes`}
      {...longPressHandlers}
    >
      <span
        className={cn(
          'block text-[10px] mb-0.5',
          isOddLast ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400',
        )}
      >
        {slotLabel(index, collect)}
      </span>
      <span className="block tabular-nums text-sm font-medium text-slate-900 dark:text-slate-100">
        {formatTime(collect.time)}
      </span>
    </button>
  );
}

export function DayTimelineCard({
  dayLabel,
  dateLabel,
  collects,
  isToday = false,
  totalLabel,
  onCollectClick,
  onCollectPeekEnd,
}: DayTimelineCardProps) {
  const sorted = [...collects].sort((a, b) => a.time.localeCompare(b.time));
  const inconsistent = isInconsistent(collects);

  return (
    <Card
      className={cn('relative', inconsistent && 'ring-1 ring-amber-200 dark:ring-amber-700/40')}
      aria-label={`Registros de ${dateLabel}`}
    >
      {inconsistent && (
        <div className="absolute bottom-0 left-0 top-0 w-1 bg-amber-400" aria-hidden="true" />
      )}

      <CardContent className={cn(inconsistent && 'pl-6')}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isToday ? (
              <span className="rounded bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">
                HOJE
              </span>
            ) : (
              <span className="text-xs font-medium uppercase text-slate-500">{dayLabel}</span>
            )}
            <span className="text-sm font-medium text-slate-900">{dateLabel}</span>
            {inconsistent && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertCircle
                    className="h-3.5 w-3.5 text-amber-500"
                    aria-label={INCONSISTENCY_MESSAGE}
                  />
                </TooltipTrigger>
                <TooltipContent>{INCONSISTENCY_MESSAGE}</TooltipContent>
              </Tooltip>
            )}
          </div>
          {totalLabel && (
            <span
              className={cn(
                'text-xs font-semibold',
                inconsistent ? 'text-slate-500' : 'text-green-600',
              )}
            >
              {totalLabel}
            </span>
          )}
        </div>

        <div
          className="flex gap-2 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
          role="list"
          aria-label={`Marcações do dia ${dateLabel}`}
        >
          {sorted.map((collect, i) => (
            <div key={collect.nsr} role="listitem">
              <CollectChip
                collect={collect}
                index={i}
                isLast={inconsistent && i === sorted.length - 1}
                inconsistent={inconsistent}
                onTap={() => onCollectClick?.(collect)}
                onLongPressRelease={onCollectPeekEnd}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
